import React, { useEffect, useState } from 'react';
import { useToggle } from '@tonic-one/react-hooks';
import { Action, O365AuditLogBannerStatus } from './type';
import { Alert, Button, Text, Flex } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { ProvisionApi } from '@Apis';
import { usePermissionStore } from '@Models/usePermissionModel';
import { ServiceForReAuth } from '@Types/administrator';
import { PermissionNameEnum } from '@Types/permissions';
import { getO365AuditLogBannerStatus } from './fns';
import { MSAuditLogProvisionModal } from './modal';
import { AUDIT_LOG_BANNER_EVENT_NAME } from './const';
import { PRODUCT_NAME } from '../../../utils/const';
import { IS_UAE } from '@Utils/const';
import { App } from '@Types/app';

export const MSAuditLogBanner = () => {
  const { orgId } = useOrgStore();
  const { widthEditablePermission, widthReadablePermission } =
    usePermissionStore();
  const [bannerStatus, setBannerStatus] = useState<O365AuditLogBannerStatus>({
    action: Action.Provision,
    show: false,
  });
  const [authUrl, setAuthUrl] = useState('');

  useEffect(() => {
    if (
      orgId === 'all' ||
      !widthEditablePermission(PermissionNameEnum.administrator) ||
      !widthReadablePermission(PermissionNameEnum.administrator)
    ) {
      setBannerStatus({
        action: Action.Provision,
        show: false,
      });
      return;
    }

    const fetchData = async () => {
      //this api has admin read permission
      const status = await getO365AuditLogBannerStatus(orgId);
      setBannerStatus(status);

      if (!status.show) return;

      let data: Awaited<
        ReturnType<typeof ProvisionApi.apiGetAuthenticationInfo>
      >['data'];
      if (status.action === Action.Provision) {
        data = (
          await ProvisionApi.apiGetAuthenticationInfo(App.O365AuditLog, orgId)
        ).data;
      } else {
        data = (
          await ProvisionApi.apiGetReAuthAuthenticationInfo(
            ServiceForReAuth.O365AuditLog,
            orgId
          )
        ).data;
      }
      setAuthUrl(data[0]?.authenticationUrl || '');
    };

    fetchData().catch((e) => console.error(e));
  }, [orgId, widthReadablePermission, widthEditablePermission]);

  if (
    !bannerStatus.show ||
    !authUrl ||
    orgId === 'all' ||
    !widthEditablePermission(PermissionNameEnum.administrator) ||
    // ADDA site not show this banner
    IS_UAE
  ) {
    return <></>;
  }

  return (
    <InnerBanner
      key={orgId}
      action={bannerStatus.action}
      authUrl={authUrl}
      orgId={orgId}
    />
  );
};

const InnerBanner: React.FC<{
  authUrl: string;
  action: Action;
  orgId: string;
}> = ({ authUrl, action, orgId }) => {
  const [isOpen, toggleOpen] = useState(true);
  const { t } = useTranslation(['dashboard', 'common']);

  useEffect(() => {
    const listener = () => {
      toggleOpen(false);
    };
    window.addEventListener(AUDIT_LOG_BANNER_EVENT_NAME, listener);
    return () => {
      window.removeEventListener(AUDIT_LOG_BANNER_EVENT_NAME, listener);
    };
  }, []);

  if (!isOpen) {
    return <></>;
  }

  return (
    <>
      <Alert variant="outline" severity="info">
        <Flex justifyContent="space-between" mt="-1" mb="-2">
          <Text>
            {t(`dashboard:dashboard.msAuditLog.banner.${action}.desc`, {
              productName: PRODUCT_NAME,
            })}
          </Text>
          <GrantAccessBtn
            {...{
              authUrl,
              action,
              orgId,
            }}
          />
        </Flex>
      </Alert>
    </>
  );
};

const GrantAccessBtn: React.FC<{
  authUrl: string;
  action: Action;
  orgId: string;
}> = ({ authUrl, action, orgId }) => {
  const [isOpen, toggleOpen] = useToggle(false);
  const { t } = useTranslation('common');

  return (
    <>
      <Button size="sm" variant="secondary" onClick={toggleOpen}>
        {action === Action.Provision
          ? t('common.action.label.grantPermission')
          : t('common.action.label.reAuth')}
        <MSAuditLogProvisionModal
          authUrl={authUrl}
          action={action}
          isOpen={isOpen}
          onModalClose={toggleOpen}
          orgId={orgId}
        />
      </Button>
    </>
  );
};
